import React from "react";
import { Box, Button, Link, Typography, useTheme } from "@mui/material";
import { IFooterList, IFooterLogoList } from "hooks/translations.interfaces";

/**
 * Footer main content block
 */
interface IFooterMainContent {
  title: string;
  description: string;
  cta: string;
  ctaUrl: string;
}

export const FooterMainContent = (props: IFooterMainContent) => {
  const { title, description, cta, ctaUrl } = props;
  const theme = useTheme();
  return (
    <Box>
      <Typography
        sx={{
          color: theme.palette.primary.main,
          mb: theme.spacing(1),
          [theme.breakpoints.up("md")]: {
            mb: theme.spacing(1),
          },
        }}
        variant="h2"
      >
        {title}
      </Typography>
      <Typography
        variant="bodyMedium"
        weight="bold"
        sx={{
          mb: theme.spacing(2.5),
          [theme.breakpoints.up("md")]: {
            mb: theme.spacing(4),
          },
        }}
      >
        {description}
      </Typography>
      <Link href={ctaUrl}>
        <Button variant="outlined">{cta}</Button>
      </Link>
    </Box>
  );
};

/**
 * Footer popular products links
 */
interface IPopularProducts {
  popularProducts: IFooterList[];
  children?: any;
}

export const PopularProducts = (props: IPopularProducts) => {
  const { popularProducts, children } = props;
  const theme = useTheme();

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {popularProducts.length > 0 && (
        <>
          {children}
          {popularProducts.map(
            (link: { title: string; link: string }, index: number) => {
              return (
                <Link
                  href={link.link}
                  target="_blank"
                  variant="titleSmall"
                  underline="hover"
                  key={"link-" + index}
                  color="inherit"
                  sx={{ mb: theme.spacing(2) }}
                >
                  {link.title}
                </Link>
              );
            },
          )}
        </>
      )}
    </Box>
  );
};

/**
 * Footer information links
 */
interface IInformation {
  information: IFooterList[];
  children?: any;
}

export const Information = (props: IInformation) => {
  const { information, children } = props;
  const theme = useTheme();

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {children}
      {information.map((link: IFooterList, index: number) => {
        return (
          <Link
            href={link.link}
            target="_blank"
            variant="titleSmall"
            underline="hover"
            key={"link-" + index}
            color="inherit"
            sx={{ mb: theme.spacing(2) }}
          >
            {link.title}
          </Link>
        );
      })}
    </Box>
  );
};

/**
 * Footer social media list
 */
interface ISocial {
  social: IFooterList[];
  children?: any;
}

export const Social = (props: ISocial) => {
  const { social, children } = props;
  const theme = useTheme();
  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {children}
      {social.map((link: IFooterList, index) => {
        return (
          <Link
            href={link.link}
            target="_blank"
            variant="titleSmall"
            underline="hover"
            key={"link-" + index}
            color="inherit"
            sx={{ mb: theme.spacing(2) }}
          >
            {link.title}
          </Link>
        );
      })}
    </Box>
  );
};

/**
 * Footer payment links
 */

interface IPaymentOptionsProps {
  paymentOptions: IFooterLogoList[];
}
export const PaymentOptions = (props: IPaymentOptionsProps) => {
  const { paymentOptions } = props;
  const theme = useTheme();

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: theme.spacing(28 / 8) }}>
      {paymentOptions.map((option, index) => {
        return (
          <Box
            component="img"
            src={option.logo}
            key={"payment-option-" + index}
            sx={{
              height: theme.spacing(44 / 8),
              [theme.breakpoints.up("md")]: {
                height: theme.spacing(22 / 8),
              },
              filter: "grayscale(1)",
              opacity: 0.8,
            }}
          />
        );
      })}
    </Box>
  );
};

/**
 * Footer payment links
 */

interface IPInternationalMembershipsProps {
  memberships: IFooterLogoList[];
}

export const InternationalMemberships = (
  props: IPInternationalMembershipsProps,
) => {
  const { memberships } = props;
  const theme = useTheme();

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: theme.spacing(28 / 8) }}>
      {memberships.map((option, index) => {
        return (
          <Link
            href={option.link}
            target="_blank"
            variant="inherit"
            underline="hover"
            key={"link-" + index}
            color="inherit"
          >
            <Box
              component="img"
              src={option.logo}
              sx={{
                height: theme.spacing(44 / 8),
                [theme.breakpoints.up("md")]: {
                  height: theme.spacing(22 / 8),
                },
                filter: "grayscale(1)",
                opacity: 0.8,
              }}
            />
          </Link>
        );
      })}
    </Box>
  );
};
